import Add16 from '@carbon/icons-react/es/add/16';
import { Col, Form, Row } from 'antd';
import React from 'react';
import { useQueryClient } from 'react-query';
import styled from 'styled-components';
import { TaskId } from 'api/models';
import { Button } from 'components/atoms/button';
import { Input } from 'components/atoms/input';
import { toastService } from 'components/molecules/toast';
import { useTranslation } from 'hooks/use-translation';
import { useJiraIssueLinkMutation } from 'queries/jira-integration';
import { getTaskDetailQueryKey } from 'queries/task';
import { JiraLinkIssueProps } from './jira-link-issue.types';

export const JiraLinkIssue = ({
  projectId,
  jiraIntegrationId,
  taskId,
  onClose,
}: JiraLinkIssueProps) => {
  const successTitle = useTranslation('success');
  const errorTitle = useTranslation('failure');
  const successMessage = useTranslation('task.link_jira_issue.success.message');
  const errorMessage = useTranslation('task.link_jira_issue.error.message');
  const placeholderText = useTranslation('task.link_jira_issue.placeholder');
  const saveText = useTranslation('task.link_jira_issue');
  const validationMessage = useTranslation('input.url');
  const cancelText = useTranslation('cancel');

  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  const { mutate: linkMutation, isLoading } = useJiraIssueLinkMutation(
    jiraIntegrationId,
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          getTaskDetailQueryKey(TaskId(projectId, taskId)),
        );
        toastService({
          title: successTitle,
          message: successMessage,
        }).success();
        onClose();
      },
      onError: () => {
        toastService({ title: errorTitle, message: errorMessage }).error();
      },
    },
  );

  const handleLink = async () => {
    const validatedFields = await form.validateFields();

    linkMutation({ taskId, ...validatedFields });
  };

  const StyledInputOnValidation = styled(Input)`
    .ant-form-item-has-error &:focus {
      outline: solid 2px;
      outline-color: var(--red-50);
    }
  `;

  return (
    <Form form={form}>
      <Row justify="end" gutter={[16, 16]}>
        <Col span={24}>
          <Form.Item
            name="issueUrl"
            rules={[{ type: 'url', message: validationMessage }]}
          >
            <StyledInputOnValidation placeholder={placeholderText} autoFocus />
          </Form.Item>
        </Col>
        <Col>
          <Button onClick={onClose} disabled={isLoading}>
            {cancelText}
          </Button>
        </Col>
        <Col>
          <Form.Item shouldUpdate>
            {() => (
              <Button
                disabled={
                  !!form.getFieldsError().filter(({ errors }) => errors.length)
                    .length
                }
                onClick={handleLink}
                loading={isLoading}
                icon={<Add16 />}
                type="primary"
                iconPlacement="right"
                htmlType="submit"
              >
                {saveText}
              </Button>
            )}
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};
